import ReactGA4 from 'react-ga4';
import { AbiItem } from 'web3-utils';
import Message from 'antd/es/message';
import {
  getAccount,
  watchAccount,
  signMessage,
  getNetwork,
  connect as wagmiConnect,
} from '@wagmi/core';
import { RpcError } from 'viem';

import { defaultInjectedConnector, rabbyKit } from '@/components/RabbykitProvider';
import { CHAINS_DICT, request, devLog, loginGa } from '@/utils';
import { deviceTypeIsMobile, getChain, getGaUserType } from '@/hooks';

import {
  connectWallet,
  deleteCurrentAddress,
  setConnectAddress,
  getconnectedDict,
} from './address';
import { isUserInstalledWallet } from './wallet';

export interface EthContract {
  addr: string;
  abi: AbiItem[];
  network: string;
}

export interface TxReqResult {
  chainId: number;
  data?: string;
  from: string;
  gas?: string;
  to: string;
  value: string;
}

export async function tryConnectToWallet() {
  if (deviceTypeIsMobile) {
    if (window.ethereum) {
      return await mobileConnect(false);
    }

    // 这里不 return，有可能走到下面的 connect （debank app 中打开页面，会带上一个登陆地址，而且也没有 window.ethereum）
    return;
  }

  // 如果没有连接过钱包, 就不自动连接了
  if (!Object.keys(getconnectedDict() || {}).length) return;
  const result = await pcConnect({ onlyReconnect: true });
  return result;
}

let flag = true;
export async function mobileConnect(showConnectModal?: boolean) {
  const onConnect = async (resolve: (v: any) => void) => {
    const { address, connector, status } = getAccount();

    if (address && status === 'connected') {
      ReactGA4.event({ category: 'User Connected', action: `User Connected_${getGaUserType()}` });
      ReactGA4.event({
        category: 'Connected Wallet',
        action: `Connected_Wallet_${connector?.name}`,
      });
      resolve(address);
    }
  };

  if (flag) {
    flag = false;
    watchAccount(async e => {
      try {
        const provider = await e.connector?.getProvider();
        let connectedProvider = `metamask`;
        if (provider?.infuraId) {
          connectedProvider = `walletconnect`;
        }

        if (e.isConnected) {
          if (e.address) {
            await connectWallet(e.address, connectedProvider);
            return;
          }

          // 用户在钱包断开连接, 会拿不到地址
          deleteCurrentAddress();
        }

        if (e.isDisconnected) {
          const connectAddr = window.ethereum?.selectedAddress;
          // metamask 切换 chain 的时候, network有时候会是loading, 如果当前还有地址, 默认不退出
          if (connectAddr) return;
          deleteCurrentAddress();
        }
      } catch (error) {
        handleConnectError(error as Error);
      }
    });
  }

  return new Promise(async (resolve, reject) => {
    const { isConnected, address, connector } = getAccount();
    const provider = await connector?.getProvider();
    let connectedProvider = `metamask`;
    if (provider?.infuraId) {
      connectedProvider = `walletconnect`;
    }

    if (isConnected && address) {
      try {
        await connectWallet(address, connectedProvider);
        onConnect(resolve);
      } catch (error) {
        handleConnectError(error as Error);
        reject();
      }
    } else if (showConnectModal) {
      rabbyKit.open({
        onConnect: () => onConnect(resolve),
        onConnectError: error => {
          handleConnectError(error);
        },
        onModalClosed() {
          reject();
        },
      });
    } else if (window.ethereum) {
      try {
        // 部分 app（imtoken） 浏览器内没有通知dapp 切换地址，直接切换完重载的页面
        // 所以要重连一次
        await wagmiConnect({ connector: defaultInjectedConnector });
        onConnect(resolve);
      } catch (e) {
        handleConnectError(e as Error);
        reject(e);
      }
    }
  });
}

let newFlag = true;
// new, only connect(get address)
export async function pcConnect({
  onlyReconnect,
  isLogin,
}: { onlyReconnect?: boolean; isLogin?: boolean } = {}): Promise<string | undefined> {
  try {
    if (newFlag) {
      watchAccount(e => {
        if (e.isConnected && e.address) {
          setConnectAddress(e.address);
        }
        if (e.isDisconnected) {
          setConnectAddress();
        }
      });
      newFlag = false;
    }
    const { address, connector, status } = getAccount();

    if (status === 'connected' && address) {
      setConnectAddress(address);

      if (onlyReconnect) {
        ReactGA4.event({ category: 'User Connected', action: `User Connected_${getGaUserType()}` });
        ReactGA4.event({
          category: 'Connected Wallet',
          action: `Connected_Wallet_${connector?.name}`,
        });
        return address;
      }
    }

    // 只重连，连不上就算了
    if (onlyReconnect) {
      return;
    }

    return new Promise((resolve, reject) => {
      const unsub = rabbyKit.subscribeModalState(open => {
        if (open) {
          if (isUserInstalledWallet()) {
            loginGa({ hasConnected: false, action: 'Login_Begin' });
          } else {
            ReactGA4.event({
              category: 'NoWallet Login Conversion',
              action: `NoWallet_Login_Begin`,
            });
          }
        }
      });
      rabbyKit.open({
        forceOpen: true,
        onConnect: async () => {
          const { connector, address } = getAccount();
          ReactGA4.event({
            category: 'User Connected',
            action: `User Connected_${getGaUserType()}`,
          });
          ReactGA4.event({
            category: 'Connected Wallet',
            action: `Connected_Wallet_${connector?.name}`,
          });
          setConnectAddress(address);
          await connectWallet(address!, connector?.getProvider ? 'wallet' : 'walletconnect');

          if (isLogin) {
            loginGa({ hasConnected: false, action: 'Connected' });
          }
          resolve(address);
        },
        onConnectError: error => {
          handleConnectError(error);
        },
        onModalClosed: () => {
          console.log('rabbykit modal  closed');
          unsub();
          reject();
        },
      });
    });
  } catch (e) {}
}

const handleConnectError = (err: Error) => {
  let error = (err?.cause || err) as RpcError;
  if (error.code === -32002) {
    Message.error('Please allow in your wallet');
  } else {
    Message.error(error.message || `connect failed`);
  }
  console.warn('[connectToWallet] error occurred', error);
  ReactGA4.event({ category: 'connect failed', action: error.message, label: error.code + '' });
};

export async function getCurrentNetVersion() {
  const { chain } = getNetwork();
  if (!chain?.id) throw new Error('not connect');
  return chain.id + '';
}

export async function isMatchNetVersion(chain: string) {
  const chainId = await getCurrentNetVersion();
  devLog('net_version', chainId, getChain(chainId), chain);

  return getChain(chainId) === chain;
}

export async function switchChain(chainId?: string) {
  if (!chainId) return;

  const account = getAccount();
  if (!account.isConnected) throw new Error('not connect');

  const config = CHAINS_DICT[chainId];
  const { chain } = getNetwork();
  if (chain && chain.id === config.id) {
    return;
  }

  const provider = await account?.connector?.getProvider();
  await provider.request({
    method: 'wallet_switchEthereumChain',
    params: [
      {
        chainId: `${config.hex}`,
      },
    ],
  });
}

export function checkConnected() {
  return getAccount().isConnected;
}

const getLoginSignText = async ({ user_addr, chainId }: { user_addr: string; chainId: string }) => {
  return (
    await request.post<{ text: string }>('/user/sign_v2', { id: user_addr, chain_id: chainId })
  ).text;
};

export async function web3UserSign(user_addr: string) {
  if (!checkConnected()) {
    throw Error(`Wallet not connected`);
  }
  const chainId = await getCurrentNetVersion();

  const text = await getLoginSignText({ user_addr, chainId });

  try {
    return await signMessage({ message: text });
  } catch (error) {
    throw (error as any)?.cause || error;
  }
}

export const web3SignText = async (text: string): Promise<any> => {
  if (!checkConnected()) {
    throw Error('Wallet not connected');
  }

  return signMessage({ message: text });
};

export const switchToChain = async (chainId: string = 'eth') => {
  try {
    await switchChain(chainId);
  } catch (switchError) {
    const chainName = CHAINS_DICT[chainId]?.name || 'Ethereum';
    Message.error(
      `Your currently selected chain is not ${chainName}, please switch to ${chainName} for the next step`,
    );
    throw Error(`Your currently selected chain is not ${chainName}`);
  }
};

const WalletList = {
  Rabby: 'isRabby',
  Coinbase: 'isCoinbaseWallet',
  trust: 'isTrust',
  imToken: 'isImToken',
  taho: 'isTally',
  XDefi: 'isXDEFI',
  Coin98: 'isCoin98',
  TokenPocket: 'isTokenPocket',
  Frame: 'isFrame',
  BitKeep: 'isBitKeep',
  Core: 'coreProvider',
};
export const getConnectedWallet = () => {
  if (!window?.ethereum) {
    return 'Unknown';
  }
  const walletName = Object.entries(WalletList).find(([_, field]) => {
    return window?.ethereum[field];
  });
  if (walletName?.[0]) {
    return walletName[0];
  }
  // 很多钱包会注入window.ethereum.isMetaMask，所以最后判断
  if (window.ethereum?.isMetaMask) {
    return 'MetaMask';
  }
  return 'Unknown';
};
